'use client';

import { useState, useEffect, useCallback } from 'react';
import { soma } from '@/lib/api';
import { Settings, Save, RotateCcw, Loader2, Zap, Shield, Brain, Wind, Bell, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

interface SettingField {
  path: string;
  label: string;
  description: string;
  type: 'boolean' | 'number' | 'select' | 'string';
  options?: string[];
  min?: number;
  max?: number;
}

const SETTING_GROUPS: { title: string; icon: any; fields: SettingField[] }[] = [
  {
    title: 'Auto-Breathe',
    icon: Wind,
    fields: [
      { path: 'breathe.auto', label: 'Auto-breathe', description: 'Automatically rotate to fresh session when context fills up', type: 'boolean' },
      { path: 'breathe.triggerAt', label: 'Trigger at %', description: 'Context % to show gentle notice', type: 'number', min: 20, max: 90 },
      { path: 'breathe.rotateAt', label: 'Rotate at %', description: 'Context % to write preload and start countdown', type: 'number', min: 40, max: 95 },
      { path: 'breathe.graceSeconds', label: 'Grace seconds', description: 'Seconds before auto-rotation', type: 'number', min: 10, max: 120 },
    ],
  },
  {
    title: 'Context Warnings',
    icon: Bell,
    fields: [
      { path: 'context.notifyAt', label: 'Notify at %', description: 'Subtle notification threshold', type: 'number', min: 20, max: 90 },
      { path: 'context.warnAt', label: 'Warn at %', description: 'Warning-level notification', type: 'number', min: 30, max: 95 },
      { path: 'context.urgentAt', label: 'Urgent at %', description: 'Urgent warning injected into prompt', type: 'number', min: 50, max: 95 },
      { path: 'context.autoExhaleAt', label: 'Auto-exhale at %', description: 'Emergency preload save', type: 'number', min: 70, max: 99 },
    ],
  },
  {
    title: 'Heat System',
    icon: Zap,
    fields: [
      { path: 'protocols.warmThreshold', label: 'Warm threshold', description: 'Heat score to show breadcrumb', type: 'number', min: 1, max: 10 },
      { path: 'protocols.hotThreshold', label: 'Hot threshold', description: 'Heat score to load full body', type: 'number', min: 3, max: 15 },
      { path: 'protocols.maxHeat', label: 'Max heat', description: 'Maximum heat accumulation', type: 'number', min: 5, max: 30 },
      { path: 'protocols.decayRate', label: 'Decay rate', description: 'Heat lost per unused session', type: 'number', min: 0, max: 5 },
      { path: 'protocols.maxFullProtocolsInPrompt', label: 'Max hot protocols', description: 'Maximum fully-loaded protocols', type: 'number', min: 1, max: 10 },
      { path: 'protocols.maxBreadcrumbsInPrompt', label: 'Max warm protocols', description: 'Maximum breadcrumb protocols', type: 'number', min: 1, max: 20 },
      { path: 'heat.autoDetect', label: 'Auto-detect', description: 'Automatically bump heat on pattern detection', type: 'boolean' },
      { path: 'heat.pinBump', label: 'Pin bump', description: 'Heat added via /pin command', type: 'number', min: 1, max: 15 },
    ],
  },
  {
    title: 'Muscles',
    icon: Brain,
    fields: [
      { path: 'muscles.tokenBudget', label: 'Token budget', description: 'Total tokens for loaded muscles', type: 'number', min: 500, max: 10000 },
      { path: 'muscles.maxFull', label: 'Max full', description: 'Muscles loaded with full body', type: 'number', min: 1, max: 10 },
      { path: 'muscles.maxDigest', label: 'Max digest', description: 'Muscles loaded as digest only', type: 'number', min: 1, max: 20 },
      { path: 'muscles.fullThreshold', label: 'Full threshold', description: 'Heat to load muscle in full', type: 'number', min: 1, max: 10 },
    ],
  },
  {
    title: 'Keepalive',
    icon: Zap,
    fields: [
      { path: 'keepalive.maxPings', label: 'Max pings', description: 'Maximum keepalive pings per idle period (0-5)', type: 'number', min: 0, max: 5 },
      { path: 'keepalive.autoExhale', label: 'Auto-exhale', description: 'Trigger exhale when pings exhausted', type: 'boolean' },
      { path: 'keepalive.autoExhaleMinTokens', label: 'Min tokens for auto-exhale', description: 'Minimum tokens before auto-exhale activates', type: 'number', min: 10000, max: 200000 },
    ],
  },
  {
    title: 'Guard',
    icon: Shield,
    fields: [
      { path: 'guard.coreFiles', label: 'Core files protection', description: 'SOMA.md, STATE.md, protocols, settings.json', type: 'select', options: ['allow', 'warn', 'block'] },
      { path: 'guard.bashCommands', label: 'Dangerous commands', description: 'rm, git push --force, etc.', type: 'select', options: ['allow', 'warn', 'block'] },
    ],
  },
  {
    title: 'Preload',
    icon: RotateCcw,
    fields: [
      { path: 'preload.staleAfterHours', label: 'Stale after hours', description: 'Hours before preload marked stale', type: 'number', min: 1, max: 168 },
    ],
  },
  {
    title: 'Checkpoints',
    icon: GitBranch,
    fields: [
      { path: 'checkpoints.enabled', label: 'Enabled', description: 'Auto-checkpoint on exhale', type: 'boolean' },
      { path: 'checkpoints.intervalMinutes', label: 'Interval (min)', description: 'Minutes between checkpoints', type: 'number', min: 1, max: 60 },
    ],
  },
];

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((o, k) => o?.[k], obj);
}

export default function SettingsPage() {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(() => {
    soma.settings()
      .then((data: any) => { setSettings(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSetting = async (path: string, value: any) => {
    setSaving(path);
    try {
      const result = await soma.updateSetting(path, value);
      if (result.ok) {
        // Update local state
        const parts = path.split('.');
        setSettings((prev: any) => {
          const next = JSON.parse(JSON.stringify(prev));
          let obj = next;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!obj[parts[i]]) obj[parts[i]] = {};
            obj = obj[parts[i]];
          }
          obj[parts[parts.length - 1]] = value;
          return next;
        });
        toast.success(`Updated ${path}`);
      } else {
        toast.error(result.error || 'Failed to save');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-gray-600 to-gray-800">
          <Settings className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Soma Settings</h1>
          <p className="text-xs text-gray-500">.soma/settings.json</p>
        </div>
      </div>

      <div className="space-y-8">
        <VibeRecordPairing />
        {SETTING_GROUPS.map(group => {
          const Icon = group.icon;
          return (
            <div key={group.title}>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                <Icon className="w-4 h-4 text-cyan-400" />
                {group.title}
              </h2>
              <div className="space-y-1">
                {group.fields.map(field => {
                  const value = getNestedValue(settings, field.path);
                  const isSaving = saving === field.path;

                  return (
                    <div key={field.path} className="flex items-center justify-between py-3 px-4 rounded-lg hover:bg-white/5 transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="text-sm text-gray-200">{field.label}</div>
                        <div className="text-[11px] text-gray-600">{field.description}</div>
                      </div>
                      <div className="flex-shrink-0">
                        {isSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                        ) : field.type === 'boolean' ? (
                          <button
                            onClick={() => updateSetting(field.path, !value)}
                            className={`w-10 h-5 rounded-full transition-colors relative ${value ? 'bg-cyan-500' : 'bg-gray-700'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${value ? 'left-5' : 'left-0.5'}`} />
                          </button>
                        ) : field.type === 'select' ? (
                          <select
                            value={value || ''}
                            onChange={e => updateSetting(field.path, e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300"
                          >
                            {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : field.type === 'number' ? (
                          <input
                            type="number"
                            value={value ?? ''}
                            min={field.min}
                            max={field.max}
                            onChange={e => {
                              const v = parseInt(e.target.value);
                              if (!isNaN(v)) updateSetting(field.path, v);
                            }}
                            className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 text-right"
                          />
                        ) : (
                          <input
                            type="text"
                            value={value ?? ''}
                            onChange={e => updateSetting(field.path, e.target.value)}
                            className="w-40 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
